import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { fetchWasteByCode } from "../../store/actions/dashboard";
import { currentYear } from "../../utils/tools";
import { Spinner } from "react-bootstrap";

const COLORS = [
  "#0B1157", "#4DFFFF", "#1a237e", "#00bcd4", "#283593",
  "#0097a7", "#303f9f", "#00838f", "#3949ab", "#006064",
];

const SELECT_STYLE = {
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "6px 12px",
  color: "#0B1157",
  fontSize: 14,
  cursor: "pointer",
  outline: "none",
};

const fmt = (num) => String(num).replace(".", ",");

const buildTooltip = (totalSum) => ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { wasteCode, wasteName, value } = payload[0].payload;
  const percent = totalSum > 0 ? (value / totalSum) * 100 : 0;
  return (
    <div style={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 10,
      padding: "10px 16px",
      boxShadow: "0 4px 16px rgba(11,17,87,0.10)",
      color: "#0B1157",
      fontSize: 13,
      maxWidth: 240,
    }}>
      <p style={{ margin: 0, fontWeight: 700, marginBottom: 4 }}>{wasteCode}</p>
      <p style={{ margin: 0, color: "#718094", marginBottom: 6 }}>{wasteName}</p>
      <p style={{ margin: 0 }}>
        <strong>{fmt(value)} t</strong>
        <span style={{ color: "#718094", marginLeft: 8 }}>({fmt(percent.toFixed(1))} %)</span>
      </p>
    </div>
  );
};

const CustomLegend = ({ payload }) => (
  <div style={{ fontSize: 12, color: "#718094", marginTop: 8 }}>
    {payload.map((entry, i) => (
      <div key={i} className="d-flex align-items-center gap-2 mb-1">
        <span style={{
          display: "inline-block", width: 10, height: 10,
          borderRadius: "50%", background: entry.color, flexShrink: 0,
        }} />
        <span style={{ color: "#0B1157" }}>{entry.payload.wasteCode}</span>
        <span style={{ marginLeft: "auto", fontWeight: 600 }}>{fmt(entry.payload.value)} t</span>
      </div>
    ))}
  </div>
);

const YEAR = currentYear();

const WasteByCodeChart = () => {
  const dispatch = useDispatch();
  const branches = useSelector((state) => state.branch.data);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(YEAR);
  const [branchId, setBranchId] = useState("");

  useEffect(() => {
    setLoading(true);
    dispatch(fetchWasteByCode({ year, branchId: branchId || null }))
      .unwrap()
      .then((res) =>
        setData(res.map((item) => ({
          ...item,
          name: `${item.wasteCode} ${item.wasteName}`,
          value: item.total,
        })))
      )
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch, year, branchId]);

  const years = Array.from({ length: 5 }, (_, i) => YEAR - i);
  const isEmpty = !loading && data.length === 0;

  return (
    <div style={{
      background: "#fff",
      borderRadius: 16,
      padding: "24px 24px 16px",
      boxShadow: "0 2px 12px rgba(11,17,87,0.07)",
      height: "100%",
    }}>
      <div className="mb-4">
        <h6 style={{ color: "#0B1157", fontWeight: 700, margin: 0, fontSize: 16 }}>
          Rozdelenie odpadu podľa kódu
        </h6>
        <p style={{ color: "#718094", fontSize: 13, margin: "2px 0 12px" }}>
          Top 10 kódov podľa objemu
        </p>
        <div className="d-flex gap-2">
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)} style={SELECT_STYLE}>
            <option value="">Všetky prevádzkarne</option>
            {branches.map((b) => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={SELECT_STYLE}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: 280 }}>
          <Spinner animation="border" style={{ color: "#0B1157" }} />
        </div>
      ) : isEmpty ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: 280, color: "#718094", fontSize: 14 }}>
          Pre zvolené obdobie nie sú žiadne dáta.
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={105}
                paddingAngle={2}
                dataKey="value"
              >
                {data.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={buildTooltip(data.reduce((sum, d) => sum + d.value, 0))} />
            </PieChart>
          </ResponsiveContainer>
          <CustomLegend payload={data.map((entry, i) => ({
            color: COLORS[i % COLORS.length],
            payload: entry,
          }))} />
        </>
      )}
    </div>
  );
};

export default WasteByCodeChart;
